/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * This library provides comprehensive event tracking for Google Analytics 4 (GA4)
 * and Google Tag Manager (GTM). All events are sent to both gtag and dataLayer
 * for maximum compatibility and flexibility.
 */

// Type declarations for window.gtag and dataLayer
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Generic event parameters interface
 */
interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Base event tracking function
 * Sends events to both gtag (GA4) and dataLayer (GTM)
 */
export const trackEvent = (
  eventName: string,
  params?: EventParams
): void => {
  // Send to Google Analytics 4 via gtag
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...params,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
    });
  }
  
  // Send to Google Tag Manager via dataLayer
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...params,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
    });
  }

  // Console log in development for debugging
  if (import.meta.env.DEV) {
    console.log('📊 Analytics Event:', eventName, params);
  }
};

/**
 * Track lead generation events (contact form, booking form submissions)
 */
export const trackLeadGeneration = (
  method: 'contact_form' | 'booking_form' | 'phone' | 'sms',
  additionalParams?: EventParams
): void => {
  const conversionValues = {
    contact_form: 20,
    booking_form: 50,
    phone: 10,
    sms: 10,
  };

  trackEvent('generate_lead', {
    method,
    currency: 'USD',
    value: conversionValues[method],
    form_destination: method.includes('form') ? method : 'call_to_action',
    ...getUTMParameters(),
    ...additionalParams,
  });
};

/**
 * Track phone number clicks with location context
 */
export const trackPhoneClick = (
  location: string,
  phoneNumber: string = '(720) 973-8622'
): void => {
  trackEvent('click', {
    link_type: 'phone',
    link_text: phoneNumber,
    link_url: `tel:+17209738622`,
    location,
    outbound: false,
    button_text: 'Call Now',
  });
};

/**
 * Track SMS clicks with location context
 */
export const trackSMSClick = (
  location: string,
  phoneNumber: string = '(720) 973-8622'
): void => {
  trackEvent('click', {
    link_type: 'sms',
    link_text: phoneNumber,
    link_url: `sms:+17209738622`,
    location,
    outbound: false,
    button_text: 'Text Us',
  });
};

/**
 * Track CTA button clicks (Book Now, etc.)
 */
export const trackCTAClick = (
  buttonText: string,
  location: string,
  destination: string
): void => {
  trackEvent('click', {
    link_type: 'cta',
    button_text: buttonText,
    link_text: buttonText,
    link_url: destination,
    location,
    outbound: false,
  });
};

/**
 * Track navigation menu clicks
 */
export const trackNavigation = (
  itemName: string,
  location: string,
  destination?: string
): void => {
  trackEvent('navigation_click', {
    item_name: itemName,
    location,
    destination: destination || `/${itemName}`,
    menu_type: location.includes('mobile') ? 'mobile' : 'desktop',
  });
};

/**
 * Track multi-step form progress
 */
export const trackFormStep = (
  step: number,
  stepName: string,
  formType: 'booking' | 'contact',
  additionalParams?: EventParams
): void => {
  // Map steps to e-commerce events for funnel tracking
  const eventMap: { [key: number]: string } = {
    1: 'begin_checkout',
    2: 'add_shipping_info',
    3: 'add_payment_info',
  };
  
  trackEvent(eventMap[step] || 'form_step', {
    step,
    step_name: stepName,
    form_type: formType,
    ...additionalParams,
  });
};

/**
 * Track form field interactions
 */
export const trackFormFieldInteraction = (
  formType: string,
  fieldName: string,
  action: 'focus' | 'blur' | 'change' = 'focus'
): void => {
  trackEvent('form_interaction', {
    form_type: formType,
    field_name: fieldName,
    interaction_type: action,
  });
};

/**
 * Track form validation errors
 */
export const trackFormError = (
  formType: string,
  errorType: string,
  fieldName?: string
): void => {
  trackEvent('form_error', {
    form_type: formType,
    error_type: errorType,
    field_name: fieldName,
  });
};

/**
 * Track gallery interactions (image views, carousel navigation)
 */
export const trackGalleryInteraction = (
  action: 'view' | 'slider_interaction' | 'carousel_click' | 'zoom' | 'expand',
  petType: string,
  location: string,
  additionalParams?: EventParams
): void => {
  trackEvent('select_content', {
    content_type: 'gallery',
    action,
    pet_type: petType,
    location,
    ...additionalParams,
  });
};

/**
 * Track pricing calculator interactions
 */
export const trackPricingCalculator = (
  action: 'service_selected' | 'size_selected' | 'addon_toggled' | 'price_calculated',
  params: EventParams
): void => {
  trackEvent('customize_product', {
    action,
    ...params,
  });
};

/**
 * Track FAQ accordion interactions
 */
export const trackFAQInteraction = (
  action: 'open' | 'close',
  question: string,
  location: string
): void => {
  trackEvent('faq_interaction', {
    action,
    question: question.substring(0, 100), // Truncate long questions
    location,
  });
};

/**
 * Track blog post engagement
 */
export const trackBlogEngagement = (
  action: 'read_start' | 'read_complete' | 'cta_click',
  postTitle: string,
  additionalParams?: EventParams
): void => {
  trackEvent('blog_engagement', {
    action,
    post_title: postTitle,
    ...additionalParams,
  });
};

/**
 * Track scroll depth milestones
 */
export const trackScrollDepth = (
  percent: number
): void => {
  trackEvent('scroll', {
    percent_scrolled: percent,
    page_path: window.location.pathname,
  });
};

/**
 * Track email link clicks
 */
export const trackEmailClick = (
  location: string,
  emailAddress: string = 'groomieroomie@gmail.com'
): void => {
  trackEvent('click', {
    link_type: 'email',
    link_text: emailAddress,
    link_url: `mailto:${emailAddress}`,
    location,
    outbound: false,
  });
};

/**
 * Track social media link clicks
 */
export const trackSocialClick = (
  platform: 'facebook' | 'instagram' | 'tiktok' | 'twitter',
  location: string
): void => {
  trackEvent('click', {
    link_type: 'social',
    platform,
    location,
    outbound: true,
  });
};

/**
 * Track map interactions
 */
export const trackMapInteraction = (
  action: 'view' | 'click' | 'directions',
  locationName: string
): void => {
  trackEvent('map_interaction', {
    action,
    location_name: locationName,
  });
};

/**
 * Capture UTM parameters from URL on first page load
 * Store in sessionStorage for attribution tracking
 */
export const captureUTMParameters = (): void => {
  if (typeof window === 'undefined') return;
  
  const params = new URLSearchParams(window.location.search);
  const utmData = {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
  };
  
  // Only store if at least one UTM parameter exists
  if (Object.values(utmData).some(val => val !== undefined)) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmData));
  }
};

/**
 * Retrieve stored UTM parameters for attribution
 */
export const getUTMParameters = (): EventParams => {
  if (typeof window === 'undefined') return {};
  
  const stored = sessionStorage.getItem('utm_params');
  return stored ? JSON.parse(stored) : {};
};

/**
 * Initialize scroll depth tracking
 * Call this once per page to set up scroll listeners
 */
export const initializeScrollTracking = (): void => {
  if (typeof window === 'undefined') return;
  
  const milestones = [25, 50, 75, 90];
  const reached: { [key: number]: boolean } = {};
  
  const checkScroll = () => {
    const scrollPercentage = 
      (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
    
    milestones.forEach(milestone => {
      if (scrollPercentage >= milestone && !reached[milestone]) {
        reached[milestone] = true;
        trackScrollDepth(milestone);
      }
    });
  };
  
  // Debounce scroll events
  let scrollTimeout: NodeJS.Timeout;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(checkScroll, 100);
  });
};

/**
 * Track service page views with additional context
 */
export const trackServiceView = (
  serviceName: string,
  additionalParams?: EventParams
): void => {
  trackEvent('view_item', {
    item_name: serviceName,
    item_category: 'service',
    ...additionalParams,
  });
};

/**
 * Track location page views with additional context
 */
export const trackLocationView = (
  locationName: string,
  additionalParams?: EventParams
): void => {
  trackEvent('view_item', {
    item_name: locationName,
    item_category: 'location',
    ...additionalParams,
  });
};
